import React, { useEffect, useMemo, useState } from 'react';
import { BUTTON_GHOST, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';
import { createCallSheet, type CallSheetTargets } from '../../utils/callSheets';
import { fetchShootsByProject } from '../../utils/projectShoots';
import type { Shoot } from '../../types/portal';

/**
 * Creating a call sheet (13 Sep 2026).
 *
 * A client is the only required field — it is the least you need to name a
 * call sheet, and a live TV day may have no project behind it at all.
 * Attaching a show or a project is how a sheet inherits crew, client contact
 * and location; leaving them empty gives a standalone sheet you fill in by
 * hand.
 *
 * Show and project narrow to the chosen client so the three fields can't
 * contradict each other, and the client is taken from the project or show
 * server-side when one is attached.
 *
 * **IT OFFERS THE DAYS THE PROJECT ALREADY HAS. 17 Oct 2026.**
 *
 * Setup and the Calendar both write `project_shoots`, and this form knew
 * nothing about them: you picked a project and then typed the date and the
 * name of the day a second time. For ATM — a build day, a tech test and
 * four live days — that was six days entered twice, with nothing noticing
 * if the two spellings drifted apart.
 *
 * Choosing a project now lists its days. Pressing one fills in the date and
 * the title; everything stays editable, and a day with a sheet already
 * made is not hidden, because a second sheet for one day is a real thing
 * (a morning unit and an evening unit) and this form should not decide it
 * is a mistake.
 */

const EMPTY = {
  clientName: '',
  showId: '',
  projectId: '',
  title: '',
  shootDate: ''
};

/** "Mon 14 Sep" — split rather than Date-parsed, so no timezone shift. */
function dayLabel(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return iso;
  return new Date(year, month - 1, day).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
}
export function CallSheetCreate({
  targets,
  onCancel,
  onCreated
}: {
  targets: CallSheetTargets;
  onCancel: () => void;
  onCreated: (id: string) => void;
}) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [shoots, setShoots] = useState<Map<string, Shoot[]>>(() => new Map());

  /* Read once. The list is small and the alternative is a fetch on every
     change of the project dropdown. */
  useEffect(() => {
    let live = true;
    void fetchShootsByProject().then(rows => {
      if (live) setShoots(rows);
    });
    return () => {
      live = false;
    };
  }, []);
  const shows = useMemo(() => targets.shows.filter(show => !form.clientName || show.clientName === form.clientName), [targets.shows, form.clientName]);
  const projects = useMemo(() => targets.projects.filter(project => (!form.clientName || project.clientName === form.clientName) && (!form.showId || project.showId === form.showId)), [targets.projects, form.clientName, form.showId]);

  /* Soonest first — the day you are making a sheet for is almost always
     the next one, not the one in July. */
  const days = useMemo(() => {
    const list = form.projectId ? shoots.get(form.projectId) ?? [] : [];
    return [...list].filter(day => day.date).sort((a, b) => a.date.localeCompare(b.date));
  }, [shoots, form.projectId]);
  function edit(patch: Partial<typeof EMPTY>) {
    setForm(current => {
      const next = {
        ...current,
        ...patch
      };
      // Changing the client invalidates a show or project chosen under the old
      // one; clearing them here beats letting the server silently override.
      if (patch.clientName !== undefined) {
        next.showId = '';
        next.projectId = '';
      }
      if (patch.showId !== undefined) next.projectId = '';
      return next;
    });
  }
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError('');
    if (!form.clientName.trim()) {
      setError('Choose a client — every call sheet belongs to one.');
      return;
    }
    setSaving(true);
    const {
      result,
      callSheet
    } = await createCallSheet({
      clientName: form.clientName.trim(),
      title: form.title.trim(),
      shootDate: form.shootDate,
      showId: form.showId || null,
      projectId: form.projectId || null
    });
    setSaving(false);
    if (!result.ok || !callSheet) {
      setError(result.error ?? 'The call sheet could not be created.');
      return;
    }
    onCreated(callSheet.id);
  }
  return <form onSubmit={submit} className="mt-10 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-581" data-mp="3pquobihggmb">
      <div className="grid grid-cols-1 gap-7 sm:grid-cols-2" data-id="element-582" data-mp="3vuuu0d7uwt5p">
        <label className="block" data-id="element-583" data-mp="3quqccjv7psdw">
          <span className="label block text-paper/40" data-id="element-584" data-mp="3qtnl2atz9nxg">Client</span>
          <select value={form.clientName} onChange={event => edit({
          clientName: event.target.value
        })} className={SELECT} data-id="element-585" data-mp="3luu3o7bklcs0">
            <option value="" className={OPTION} data-id="element-586" data-mp="26c5yiqdq9706">
              Choose a client…
            </option>
            {targets.clients.map(client => <option key={client} value={client} className={OPTION} data-id="element-587" data-mp="2bck6k8fbwgy7">
                {client}
              </option>)}
          </select>
        </label>

        <label className="block" data-id="element-588" data-mp="1in3jlqah9s9d">
          <span className="label block text-paper/40" data-id="element-589" data-mp="1io4x01eyuqu9">Date</span>
          <input type="date" value={form.shootDate} onChange={event => edit({
          shootDate: event.target.value
        })} className={`${FIELD} [color-scheme:dark]`} data-id="element-590" data-mp="1nl53gdfq9vlw" />
        </label>

        <label className="block sm:col-span-2" data-id="element-591" data-mp="1wfjfm74yoq0s">
          <span className="label block text-paper/40" data-id="element-592" data-mp="zwc2u3o5uzky">What is this day?</span>
          <input value={form.title} onChange={event => edit({
          title: event.target.value
        })} placeholder="e.g. Live — Monday, Tech test, Build day" className={FIELD} data-id="element-593" data-mp="zu9fjree7kjx" />
        </label>

        <label className="block" data-id="element-594" data-mp="22a9xe7lrhubf">
          <span className="label block text-paper/40" data-id="element-595" data-mp="2298an5vdvjdv">Show (optional)</span>
          <select value={form.showId} onChange={event => edit({
          showId: event.target.value
        })} disabled={shows.length === 0} className={`${SELECT} disabled:opacity-40`} data-id="element-596" data-mp="2gxcw2hltm90c">
            <option value="" className={OPTION} data-id="element-597" data-mp="1obtgxoy3kpg3">
              — none —
            </option>
            {shows.map(show => <option key={show.id} value={show.id} className={OPTION} data-id="element-598" data-mp="1xa5gwhkuszl7">
                {show.name}
              </option>)}
          </select>
        </label>

        <label className="block" data-id="element-599" data-mp="1tawn5cptd1pn">
          <span className="label block text-paper/40" data-id="element-600" data-mp="38pao9yvxjmb5">Project (optional)</span>
          <select value={form.projectId} onChange={event => edit({
          projectId: event.target.value
        })} disabled={projects.length === 0} className={`${SELECT} disabled:opacity-40`} data-id="element-601" data-mp="38rew2rwy95gp">
            <option value="" className={OPTION} data-id="element-602" data-mp="3h7mb0q8b3jj6">
              — none —
            </option>
            {projects.map(project => <option key={project.id} value={project.id} className={OPTION} data-id="element-603" data-mp="32b53yb75gr2f">
                {project.name}
              </option>)}
          </select>
        </label>
      </div>

      {/* The days this project already has. Typing them again is how two
          spellings of the same day end up in the studio. */}
      {days.length > 0 && <div className="mt-9" data-id="element-604" data-mp="3l8y3r4mh8kvl">
          <p className="label m-0 text-paper/40" data-id="element-605" data-mp="3l7myf0ddv52d">Its shoot days</p>
          <div className="mt-4 flex flex-wrap gap-2.5" data-id="element-606" data-mp="3q6hh82bcotmh">
            {days.map(day => {
          const on = form.shootDate === day.date;
          return <button key={day.id} type="button" onClick={() => edit({
            shootDate: day.date,
            title: day.title.trim() || form.title
          })} aria-pressed={on} className={['flex flex-col items-start border px-4 py-3 text-left transition-colors duration-150 ease-out', on ? 'border-paper bg-paper text-ink' : 'border-ruleDark text-paper/45 hover:border-paper/60 hover:text-paper'].join(' ')} data-id="element-607" data-mp="mi656li940v5">
                  <span className="text-[0.95rem] leading-none" data-id="element-608" data-mp="2xilafh5h7z42">{dayLabel(day.date)}</span>
                  {day.title.trim() && <span className={`label-sm mt-2 ${on ? 'text-ink' : 'text-paper/40'}`} data-id="element-609" data-mp="2sifhlxnh06ke">
                      {day.title.trim()}
                    </span>}
                </button>;
        })}
          </div>
        </div>}

      <p className="label-sm mt-6 max-w-xl leading-relaxed text-paper/35" data-id="element-610" data-mp="1c5i9cwtrune4">
        {form.projectId ? 'Crew, client contact and location come from the project. You can add anyone else on the call sheet itself.' : 'No project attached — you’ll pick the crew and type the contacts on the call sheet itself.'}
      </p>

      {error && <p role="alert" className="mt-6 label-sm text-paper/70" data-id="element-611" data-mp="1bxwpofp3eulf">
          {error}
        </p>}

      <div className="mt-8 flex flex-wrap items-center gap-5" data-id="element-612" data-mp="1griuxmsxfw77">
        <button type="submit" disabled={saving} className={BUTTON_SOLID} data-id="element-613" data-mp="1ksl00k9iz2h3">
          {saving ? 'Creating…' : 'Create Call Sheet'}
        </button>
        <button type="button" onClick={onCancel} className={BUTTON_GHOST} data-id="element-614" data-mp="1psz4xtftxwaw">
          Cancel
        </button>
      </div>
    </form>;
}